// src/components/DesignAnalysisPanel.jsx
import React, { useState, useEffect } from 'react';
import ComplianceCard from './ComplianceCard.jsx';
import ClimateResultsView from './ClimateResultsView.jsx';
import PlanParseView from './PlanParseView.jsx';
import UtilitiesPanel from './UtilitiesPanel.jsx';
import VastuPanel from './VastuPanel.jsx';
import { TAB_ICON } from './AnalysisTabIcons.jsx';
import { API_BASE } from '../utils/apiBase';

// same backend origin Site3DView uses. TODO(centralise): move to utils/apiBase.js
// The assistant is no longer a tab — it's the shared strip at the bottom of the
// drawer, so any analysis can be asked about without leaving it.
const TABS = [
  { key: 'design', label: 'Design', ready: true },
  { key: 'compliance', label: 'Compliance', ready: true },
  { key: 'climate', label: 'Climate', ready: true },
  // Beside Climate because it answers the same kind of question — how the
  // building sits to the sun, the wind and its surroundings — from a different
  // tradition. It is NOT beside Compliance, and that placement is the point:
  // nothing in it is a regulation.
  { key: 'vastu', label: 'Vastu', ready: true },
  { key: 'water', label: 'Water', ready: true },
  { key: 'electricity', label: 'Electricity', ready: true },
];

/**
 * DesignAnalysisPanel — content of the bottom drawer opened by the "Design Analysis" button.
 * Mirrors SheetPanel: flex column, header bar + scrolling body, same tokens.
 *
 * Data flow:
 *   Site3DView.buildFloorStack -> window 'complianceInputsChanged' { achieved, facts }
 *     -> held in `inputs` (seeded from project.cachedCompliance so a reopen shows the last build)
 *   facts + location -> GET /api/dcr/resolve -> paramFile (only used if it's the
 *     resolved shape, i.e. has `.parameters`; otherwise stays null -> card shows
 *     achieved-only "no DCR yet")
 *   -> <ComplianceCard paramFile achieved facts />
 *
 * The DCR Bot tab answers free-form questions that go beyond the encoded
 * parameters, via POST /api/dcr/ask (retrieval over the source PDFs + cited
 * phrasing). See DcrBotPanel.jsx.
 */
export default function DesignAnalysisPanel({ project }) {
  const [tab, setTab] = useState('design');   // Design is the first tab
  const [inputs, setInputs] = useState(() => project?.cachedCompliance || null);
  const [paramFile, setParamFile] = useState(null);

  // another pane (PaneRight) can ask the drawer to open on a given tab
  useEffect(() => {
    const onOpenTab = (e) => { if (e.detail?.tab) setTab(e.detail.tab); };
    window.addEventListener('openAnalysisTab', onOpenTab);
    return () => window.removeEventListener('openAnalysisTab', onOpenTab);
  }, []);

  // seed from cache when the project changes
  useEffect(() => {
    setInputs(project?.cachedCompliance || null);
  }, [project?.id]);

  // achieved + facts from the massing (Site3DView emits on every build)
  useEffect(() => {
    const onInputs = (e) => setInputs(e.detail || null);
    window.addEventListener('complianceInputsChanged', onInputs);
    return () => window.removeEventListener('complianceInputsChanged', onInputs);
  }, []);

  // resolve the DCR param file for this project's location + facts
  useEffect(() => {
    if (!project?.centerLat || !project?.centerLng) return;
    const facts = inputs?.facts || {};
    const params = new URLSearchParams({ lat: project.centerLat, lng: project.centerLng });
    if (facts.road_width_m != null) params.set('roadWidthM', facts.road_width_m);
    if (facts.building_height_m != null) params.set('heightM', facts.building_height_m);
    if (facts.plot_area_m2 != null) params.set('plotAreaM2', facts.plot_area_m2);
    if (facts.use) params.set('useType', facts.use);
    if (facts.zone) params.set('zone', facts.zone);

    let cancelled = false;
    fetch(`${API_BASE}/api/dcr/resolve?${params.toString()}`)
      .then((r) => (r.ok ? r.json() : null))
      // accept only a resolved file that actually defines at least one parameter;
      // otherwise stay null -> ComplianceCard skeleton mode (achieved-only)
      .then((pf) => {
        const hasParams =
          pf && pf.parameters &&
          ['exterior', 'interior', 'amenities'].some(
            (c) => Object.keys(pf.parameters[c] || {}).length
          );
        if (!cancelled) setParamFile(hasParams ? pf : null);
      })
      .catch(() => { if (!cancelled) setParamFile(null); });
    return () => { cancelled = true; };
  }, [project?.centerLat, project?.centerLng, inputs?.facts]);

  if (!project) {
    return (
      <div style={{ padding: 16, fontSize: 13, color: 'var(--text-secondary,#c3c9d4)' }}>
        Select a project.
      </div>
    );
  }

  const tabBtn = (active, ready) => ({
    // inline-flex so the icon and the label share a baseline row; without it the
    // svg sits as a block and pushes the text onto a second line at narrow widths
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
    fontSize: 12,
    padding: '5px 10px 5px 9px',
    borderRadius: 8,
    cursor: ready ? 'pointer' : 'not-allowed',
    border: '0.5px solid var(--border,#2c3444)',
    background: active ? 'var(--surface-2,#1a2234)' : 'transparent',
    color: active
      ? 'var(--text,#e5e9f0)'
      : ready
      ? 'var(--text-secondary,#c3c9d4)'
      : 'var(--text-muted,#8b92a0)',
    opacity: ready ? 1 : 0.6,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* header bar */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px', flexShrink: 0,
          borderBottom: '1px solid var(--border,#2c3444)',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text,#e5e9f0)', marginRight: 4 }}>
          Design Analysis
        </span>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            disabled={!t.ready}
            onClick={() => t.ready && setTab(t.key)}
            style={tabBtn(tab === t.key, t.ready)}
            title={t.ready ? '' : 'Coming soon'}
          >
            {TAB_ICON[t.key] ? React.createElement(TAB_ICON[t.key]) : null}
            <span>{t.label}{!t.ready && ' — soon'}</span>
          </button>
        ))}
      </div>

      {/* scrolling body */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 12 }}>
        {tab === 'design' && <PlanParseView project={project} apiBase={API_BASE} />}

        {tab === 'compliance' && (
          <div style={{ maxWidth: 460 }}>
            {!inputs ? (
              <div style={{ fontSize: 12, color: 'var(--text-secondary,#c3c9d4)' }}>
                Build the massing (left panel → Build) to compute the exterior
                values, then they’ll be checked here.
              </div>
            ) : (
              <ComplianceCard
                paramFile={paramFile}
                achieved={inputs.achieved}
                facts={inputs.facts}
                zoneConfidence={project?.zoneConfidence}
              />
            )}
          </div>
        )}

        {/* Results ONLY — the controls stay in the right pane. Mounting a
            second interactive copy here would be unsafe: those panels call
            deactivate() on unmount, so closing one would kill the other's
            3D visualisation. This view only subscribes to the stores. */}
        {tab === 'climate' && <ClimateResultsView project={project} />}

        {tab === 'vastu' && (
          <VastuPanel project={project} apiBase={API_BASE} inputs={inputs} />
        )}

        {tab === 'water' && (
          <UtilitiesPanel project={project} apiBase={API_BASE} kind="water" />
        )}

        {tab === 'electricity' && (
          <UtilitiesPanel project={project} apiBase={API_BASE} kind="electricity" />
        )}
      </div>

      {/* The assistant used to live here, as a strip at the bottom of this
          drawer. It now sits under the 3D view (PaneMain), because the questions
          it answers — what setback applies, what FSI is permitted — come up
          before any analysis has been run, and reaching it meant opening a panel
          about work that had not happened yet. */}
    </div>
  );
}
